import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { describeChanges, isRevertable, type LogEntry, type Person, type User } from '@crmsy/shared';
import { db } from '../lib/db.js';
import { dispatch } from '../lib/actions.js';
import { toast } from '../lib/toast.js';
import { RevertConfirm, changeLabels } from './Dialogs.jsx';
import { logTypeLabel, t, useT } from '../i18n/index.js';

export function when(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return sameDay ? time : `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} ${time}`;
}

export function canRevert(e: LogEntry, user: User): boolean {
  if (e.pending || !isRevertable(e)) return false;
  return user.role === 'admin' || (e.actorEmail ?? '').toLowerCase() === user.email.toLowerCase();
}

export function actorName(e: LogEntry, people: Person[]): string {
  if (e.actor) return people.find((p) => p.id === e.actor)?.name ?? e.actorEmail ?? t('timeline.someone');
  return e.actorEmail ?? (e.source === 'notion' ? t('timeline.notion') : t('timeline.system'));
}

const TYPE_ICON: Record<LogEntry['type'], string> = {
  note: '✎', create: '＋', update: '✎', 'follow-up': '✓', snooze: '😴', archive: '🗑', restore: '↺', suggestion: '✨', external: 'N', revert: '↶', message: '✉',
};

export function OriginBadge({ e }: { e: LogEntry }) {
  if (!e.origin) return null;
  return <span className="rounded-full bg-surface px-1.5 py-0.5 text-[10px] font-medium text-muted">{t(`origin.${e.origin}`)}</span>;
}

export function StatusBadge({ e }: { e: LogEntry }) {
  if (e.pending) return <span className="rounded-full bg-warn-soft px-1.5 py-0.5 text-[10px] font-medium text-warn-ink">{t('badge.pending')}</span>;
  if (e.reverted) return <span className="rounded-full bg-surface px-1.5 py-0.5 text-[10px] font-medium text-muted">{t('badge.reverted')}</span>;
  if (e.conflict) return <span className="rounded-full bg-danger-soft px-1.5 py-0.5 text-[10px] font-medium text-danger" title={t('badge.conflictTitle')}>{t('badge.conflict')}</span>;
  return null;
}

/** Notes + changes for one contact, newest first (§6 panel). */
export function Timeline({ contactId, people, user }: { contactId: string; people: Person[]; user: User }) {
  const t = useT();
  const entries = useLiveQuery(() => db.log.where('contactId').equals(contactId).toArray(), [contactId], undefined);
  const [confirm, setConfirm] = useState<LogEntry | null>(null);
  if (!entries) return null;
  const visible = entries.filter((e) => !e.archived).sort((a, b) => b.at.localeCompare(a.at));
  const name = (id: string) => people.find((p) => p.id === id)?.name ?? id;
  if (!visible.length) {
    const [before, after] = t('timeline.empty', { key: '\u0000' }).split('\u0000');
    return (
      <p className="text-sm text-muted">
        {before}
        <kbd>c</kbd>
        {after}
      </p>
    );
  }
  return (
    <>
      <ol className="space-y-2">
        {visible.map((e) => (
          <li key={e.id} className={`rounded-lg border border-line px-3 py-2 text-sm ${e.reverted ? 'opacity-60' : ''}`} data-log-type={e.type}>
            <div className="flex items-center gap-2 text-xs text-muted">
              <span className="w-4 text-center">{TYPE_ICON[e.type]}</span>
              <span className="font-medium text-ink">{actorName(e, people)}</span>
              <span>{logTypeLabel(e.type).toLowerCase()}</span>
              <span>· {when(e.at)}</span>
              <OriginBadge e={e} />
              <StatusBadge e={e} />
              <span className="flex-1" />
              {canRevert(e, user) && (
                <button type="button" className="hover:text-accent-dark" onClick={() => setConfirm(e)}>
                  {t('timeline.revert')}
                </button>
              )}
            </div>
            {e.text && <p className="mt-1 whitespace-pre-wrap">{e.text}</p>}
            {e.changes.length > 0 && (
              <ul className="mt-1 space-y-0.5 text-xs text-muted">
                {describeChanges(e.changes, name, changeLabels()).map((l, i) => (
                  <li key={i}>{l}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>
      {confirm && (
        <RevertConfirm
          entry={confirm}
          people={people}
          onConfirm={() => void dispatch('revert', { entryId: confirm.id }).catch((e: unknown) => toast(String(e instanceof Error ? e.message : e), { kind: 'error' }))}
          onClose={() => setConfirm(null)}
        />
      )}
    </>
  );
}
